/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action, IObservableArray} from 'mobx';

/**************************************** PROJECT MODULES *****************************************/
import {Store} from '../root-store';
import {navIfNoUnsavedData} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
type CloseModalStack = IObservableArray<{closeModal: Function; id?: string}>;

/********************************************* EXPORT *********************************************/
/**
 * Singleton store for modal UI data
 * Handles opening & closing global modals, determines their open/closed state
 * Stores stack of all open modals (partially implemented)
 */
export class ModalStore {
    public static Instance: ModalStore | null = null;

    /**
     * "Stack" of closeModal functions used to close currently open modals
     * Used for determining which modal to shut when Escape key is pressed
     *
     * Popping topmost closeModal function off the stack and running it should
     * close the topmost modal
     */
    @observable
    public closeModalStack: CloseModalStack = ([] as any) as CloseModalStack;

    /**
     * If set to true, open create new record modal sequence/flow
     */
    @observable
    public isCreateNewRecordOpen: boolean = false;

    /**
     * If true, open consent modal appearing when dependent record turns 16
     */
    @observable
    public isConsentWarningModalOpen: boolean = false;

    /**
     * If true, open PHU reconsent modal appearing when record has turned 16
     * and PHU reporting age is > 16
     */
    @observable
    public isPHUReconsentModalOpen: boolean = false;

    /**
     * If set to true, open relationship modal appearing if record has no
     * relationship set
     */
    @observable
    public isRelationshipWarningModalOpen: boolean = false;

    /**
     * If set to true, open "PHU successfully enabled" modal
     */
    @observable
    public isPHUEnabledModalOpen: boolean = false;

    /************* MUTATIVE ACTIONS *************/
    /**
     * Display the create new record modal
     */
    @action
    public openCreateNewRecord = () => {
        navIfNoUnsavedData(
            this.rootStore,
            () => {
                this.isCreateNewRecordOpen = true;
                this.rootStore.uiStore.setIsOuterScrollActive(false);
            },
            true
        );
    };

    /**
     * Hide the create new record modal
     */
    @action
    public hideCreateNewRecord = () => {
        this.isCreateNewRecordOpen = false;
        this.rootStore.uiStore.setIsOuterScrollActive(true);
    };

    /**
     * Display the consent warning modal
     */
    @action
    public openConsentWarning = () => {
        this.isConsentWarningModalOpen = true;
        this.rootStore.uiStore.setIsOuterScrollActive(false);
    };

    /**
     * Hide the consent warning modal
     */
    @action
    public hideConsentWarning = () => {
        this.isConsentWarningModalOpen = false;
        this.rootStore.uiStore.setIsOuterScrollActive(true);
    };

    /**
     * Display the consent warning modal
     */
    @action
    public openPhuReconsentWarning = () => {
        this.isPHUReconsentModalOpen = true;
        this.rootStore.uiStore.setIsOuterScrollActive(false);
    };

    /**
     * Hide the consent warning modal
     */
    @action
    public hidePhuReconsentWarning = () => {
        this.isPHUReconsentModalOpen = false;
        this.rootStore.uiStore.setIsOuterScrollActive(true);
    };

    /**
     * Display the 'relationship missing' warning modal
     */
    @action
    public openRelationshipWarning = () => {
        this.isRelationshipWarningModalOpen = true;
        this.rootStore.uiStore.setIsOuterScrollActive(false);
    };

    /**
     * Hide the 'relationship missing' warning modal
     */
    @action
    public hideRelationshipWarning = () => {
        this.isRelationshipWarningModalOpen = false;
        this.rootStore.uiStore.setIsOuterScrollActive(true);
    };

    /**
     * Display the "PHU successfully enabled" modal
     */
    @action
    public openPHUEnabledModal = () => {
        this.isPHUEnabledModalOpen = true;
        this.rootStore.uiStore.setIsOuterScrollActive(false);
    };

    /**
     * Hide the "PHU successfully enabled" modal
     */
    @action
    public hidePHUEnabledModal = () => {
        this.isPHUEnabledModalOpen = false;
        this.rootStore.uiStore.setIsOuterScrollActive(true);
    };

    /************* GENERIC OPEN / CLOSE MODAL *************/
    /**
     * Remove closeModal function with the matching ID from the modalCloseStack
     */
    public removeCloseModalFn = (id: string) => {
        const idx = this.closeModalStack.findIndex(item => item.id === id);
        if (idx || idx === 0) this.closeModalStack.splice(idx, 1);
    };

    /**
     * Pop & run the topmost modal close function on the modalCloseStack
     * Turn outer scroll back on
     */
    public closeModal = () => {
        this.closeModalStack.length && this.closeModalStack.pop().closeModal();
        this.rootStore.uiStore.setIsOuterScrollActive(true); // Unpause outer scroll
        this.rootStore.uiStore.hotkeyStore.unpauseHotkeys(); // Unpause hotkeys
    };

    /**
     * Push modal close function onto the modalCloseStack
     * Popping this function off the stack and running it should close the
     * corresponding modal
     */
    public addModalCloseFn = (closeModal: Function, id?: string) => {
        if (id && this.closeModalStack.some(modalObj => modalObj.id === id)) return;
        return this.closeModalStack.push({closeModal, id});
    };

    /************* INSTANTIATION *************/
    /**
     * Factory method (true constructor) for singleton ModalStore
     */
    public static new = (rootStore: Store): ModalStore => {
        if (!ModalStore.Instance) {
            ModalStore.Instance = new ModalStore(rootStore);
        }
        return ModalStore.Instance;
    };
    private constructor(public rootStore: Store) {}
}
